import { Component, computed, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { CartService } from '../../../../shared/services/products/cart.service';
import { OrdersService } from '../../../../shared/services/products/orders.service';
import { AuthService } from '../../../auth/services/auth/auth.service';
import { DeliveryMethod } from '../../../../shared/interfaces/product.interface';
import { PricePipe } from '../../../../shared/pipes/price.pipe';
import { HAIRSTYLE_PLACEHOLDER } from '../../../../shared/constants/app.constants';

@Component({
  selector: 'braidsbook-cart-drawer',
  imports: [NgOptimizedImage, FormsModule, RouterLink, TranslatePipe, PricePipe],
  template: `
    @if (isOpen()) {
      <div class="fixed inset-0 z-[150] bg-black/40" (click)="close()" aria-hidden="true"></div>
      <aside class="fixed inset-y-0 right-0 z-[151] w-full max-w-md bg-white shadow-2xl flex flex-col"
        role="dialog" aria-modal="true" [attr.aria-label]="'SHOP.CART.TITLE' | translate">
        <div class="flex items-center justify-between px-5 h-16 border-b border-gray-100">
          <div>
            <div class="text-lg font-semibold text-main-900">{{ 'SHOP.CART.TITLE' | translate }}</div>
            @if (cart(); as c) {
              <div class="text-xs text-gray-500">{{ 'SHOP.CART.SELLER' | translate: { seller: c.sellerName } }}</div>
            }
          </div>
          <button type="button" (click)="close()" class="size-9 rounded-full hover:bg-gray-100 text-xl" aria-label="Fermer">×</button>
        </div>

        @if (!cart()) {
          <div class="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div class="text-4xl mb-3">🛍️</div>
            <div class="font-medium">{{ 'SHOP.CART.EMPTY' | translate }}</div>
            <div class="text-sm text-gray-500 mb-5">{{ 'SHOP.CART.EMPTY_HINT' | translate }}</div>
            <a [routerLink]="['/boutique']" (click)="close()" class="rounded-full bg-main-500 hover:bg-main-600 text-white px-6 py-2.5 text-sm">
              {{ 'SHOP.CART.BROWSE' | translate }}
            </a>
          </div>
        } @else {
          <div class="flex-1 overflow-y-auto px-5 py-4" data-lenis-prevent>
            <ul class="divide-y divide-gray-100 mb-6">
              @for (line of cart()!.lines; track line.productId) {
                <li class="py-3 flex gap-3">
                  <div class="relative size-16 shrink-0 rounded-lg overflow-hidden bg-primary-light">
                    <img [ngSrc]="line.imageUrl || placeholder" [alt]="line.name" fill class="object-cover">
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-sm line-clamp-2">{{ line.name }}</div>
                    <div class="text-xs text-gray-500">{{ line.price | price: line.currency }}</div>
                    <div class="flex items-center justify-between mt-1.5">
                      <div class="flex items-center rounded-full border border-gray-200 text-sm">
                        <button type="button" (click)="setQuantity(line.productId, line.quantity - 1)" class="size-7" aria-label="-">−</button>
                        <span class="w-6 text-center">{{ line.quantity }}</span>
                        <button type="button" (click)="setQuantity(line.productId, line.quantity + 1)"
                          [disabled]="line.stock !== null && line.quantity >= line.stock" class="size-7 disabled:opacity-30" aria-label="+">+</button>
                      </div>
                      <button type="button" (click)="remove(line.productId)" class="text-xs text-gray-500 hover:text-red-600">
                        {{ 'SHOP.CART.REMOVE' | translate }}
                      </button>
                    </div>
                  </div>
                </li>
              }
            </ul>

            <div class="mb-5">
              <div class="text-sm font-medium mb-2">{{ 'SHOP.CART.DELIVERY_METHOD' | translate }}</div>
              <div class="grid grid-cols-2 rounded-full bg-gray-100 p-1 text-sm">
                @for (m of methods; track m.value) {
                  <button type="button" (click)="deliveryMethod.set(m.value)" class="rounded-full py-2 transition-colors"
                    [class]="deliveryMethod() === m.value ? 'bg-white shadow font-medium text-main-900' : 'text-gray-600'">
                    {{ m.label | translate }}
                  </button>
                }
              </div>
            </div>

            @if (deliveryMethod() === 'delivery') {
              <label class="block mb-4">
                <span class="text-sm font-medium">{{ 'SHOP.CART.ADDRESS' | translate }}</span>
                <input type="text" [(ngModel)]="address" name="address" autocomplete="street-address"
                  class="mt-1 w-full rounded-lg border border-gray-200 px-3 h-11 text-sm focus:outline-none focus:border-main-500"
                  [class.border-red-400]="addressError()">
                @if (addressError()) {
                  <span class="text-xs text-red-600">{{ 'SHOP.CART.ADDRESS_REQUIRED' | translate }}</span>
                }
              </label>
            }

            <label class="block">
              <span class="text-sm font-medium">{{ 'SHOP.CART.NOTE' | translate }}</span>
              <textarea [(ngModel)]="note" name="note" rows="2" maxlength="500"
                class="mt-1 w-full rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:border-main-500"></textarea>
            </label>
          </div>

          <div class="border-t border-gray-100 px-5 py-4">
            <div class="flex items-center justify-between mb-1">
              <span class="text-gray-600">{{ 'SHOP.CART.SUBTOTAL' | translate }}</span>
              <span class="text-xl font-semibold text-main-900">{{ subtotal() | price: currency() }}</span>
            </div>
            <p class="text-xs text-gray-500 mb-3">{{ 'SHOP.PAYMENT_NOTICE' | translate }}</p>
            @if (!isLoggedIn()) {
              <button type="button" (click)="goToLogin()" class="w-full h-12 rounded-full bg-main-500 hover:bg-main-600 text-white font-medium">
                {{ 'SHOP.CART.LOGIN_TO_ORDER' | translate }}
              </button>
            } @else if (!isClient()) {
              <p class="text-sm text-center text-gray-600 py-2">{{ 'SHOP.CART.CLIENTS_ONLY' | translate }}</p>
            } @else {
              <button type="button" (click)="placeOrder()" [disabled]="isSaving()"
                class="w-full h-12 rounded-full bg-main-500 hover:bg-main-600 text-white font-medium disabled:opacity-60">
                {{ 'SHOP.CART.ORDER' | translate }}
              </button>
            }
          </div>
        }
      </aside>
    }
  `
})
export class CartDrawerComponent {
  readonly #cartService = inject(CartService);
  readonly #ordersService = inject(OrdersService);
  readonly #authService = inject(AuthService);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);

  isOpen = this.#cartService.isOpen;
  cart = this.#cartService.cart;
  subtotal = this.#cartService.subtotal;
  currency = this.#cartService.currency;
  isSaving = this.#ordersService.isSaving;
  isLoggedIn = this.#authService.isUserLoggedIn;
  isClient = computed(() => this.#authService.currentUser()?.user_type === 'client');

  deliveryMethod = signal<DeliveryMethod>('pickup');
  addressError = signal(false);
  address = '';
  note = '';

  readonly methods: { value: DeliveryMethod; label: string }[] = [
    { value: 'pickup', label: 'SHOP.CART.PICKUP' },
    { value: 'delivery', label: 'SHOP.CART.DELIVERY' }
  ];
  protected readonly placeholder = HAIRSTYLE_PLACEHOLDER;

  @HostListener('document:keydown.escape')
  close(): void {
    this.#cartService.close();
  }

  setQuantity(productId: number, quantity: number): void {
    this.#cartService.setQuantity(productId, quantity);
  }

  remove(productId: number): void {
    this.#cartService.remove(productId);
  }

  goToLogin(): void {
    this.close();
    this.#router.navigate(['/auth/login'], { queryParams: { returnUrl: this.#router.url } });
  }

  placeOrder(): void {
    const cart = this.cart();
    if (!cart) return;
    const address = this.address.trim();
    if (this.deliveryMethod() === 'delivery' && !address) {
      this.addressError.set(true);
      return;
    }
    this.addressError.set(false);
    this.#ordersService.createOrder({
      items: cart.lines.map(l => ({ product_id: l.productId, quantity: l.quantity })),
      delivery_method: this.deliveryMethod(),
      delivery_address: this.deliveryMethod() === 'delivery' ? address : undefined,
      note: this.note.trim() || undefined
    }).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#cartService.clear();
      this.note = '';
      this.close();
      this.#router.navigate(['/profile/customer/orders']);
    });
  }
}
